/* ==========================================================================
   VITAL VIAL - Core runtime
   Zero dependencies. Defer-loaded. Everything degrades gracefully without it.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('vv-js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll reveal ------------------------------------------ */
  function initReveal() {
    var nodes = document.querySelectorAll('[data-vv-reveal], .vv-reveal-lines, .vv-thread');
    if (!nodes.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-in');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    nodes.forEach(function (n) {
      // Stagger children that opt in via data-vv-stagger on the parent
      var stagger = n.getAttribute('data-vv-stagger');
      if (stagger) {
        var kids = n.children;
        for (var k = 0; k < kids.length; k++) {
          kids[k].style.setProperty('--vv-reveal-delay', (k * parseInt(stagger, 10)) + 'ms');
        }
      }
      io.observe(n);
    });
  }

  /* ---------- 2. Gold thread path length -------------------------------- */
  function initThreads() {
    document.querySelectorAll('.vv-thread path').forEach(function (p) {
      try {
        var len = Math.ceil(p.getTotalLength());
        p.closest('.vv-thread').style.setProperty('--vv-thread-len', len);
      } catch (e) { /* non-rendered SVG */ }
    });
  }

  /* ---------- 3. Image fade-in on decode -------------------------------- */
  function initImages() {
    document.querySelectorAll('img.vv-fade-in, .vv-media[data-vv-loading="true"] > img').forEach(function (img) {
      var done = function () {
        img.classList.add('is-loaded');
        var m = img.closest('.vv-media');
        if (m) m.setAttribute('data-vv-loading', 'false');
      };
      if (img.complete && img.naturalWidth) { done(); }
      else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
    });
  }

  /* ---------- 4. Accordion (details-free, fully animated) ---------------- */
  function initAccordions() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-vv-accordion-trigger]');
      if (!trigger) return;
      var item = trigger.closest('[data-vv-accordion-item]');
      var group = trigger.closest('[data-vv-accordion]');
      if (!item) return;

      var isOpen = item.hasAttribute('data-open');
      var single = group && group.getAttribute('data-vv-accordion') === 'single';

      if (single) {
        group.querySelectorAll('[data-vv-accordion-item][data-open]').forEach(function (other) {
          if (other !== item) closePanel(other);
        });
      }
      isOpen ? closePanel(item) : openPanel(item);
    });

    function panelOf(item) { return item.querySelector('[data-vv-accordion-panel]'); }

    function openPanel(item) {
      var panel = panelOf(item), trig = item.querySelector('[data-vv-accordion-trigger]');
      item.setAttribute('data-open', '');
      if (trig) trig.setAttribute('aria-expanded', 'true');
      if (!panel) return;
      panel.hidden = false;
      panel.style.height = reduceMotion ? 'auto' : panel.scrollHeight + 'px';
      if (!reduceMotion) {
        panel.addEventListener('transitionend', function h(ev) {
          if (ev.propertyName !== 'height') return;
          panel.style.height = 'auto';
          panel.removeEventListener('transitionend', h);
        });
      }
    }

    function closePanel(item) {
      var panel = panelOf(item), trig = item.querySelector('[data-vv-accordion-trigger]');
      item.removeAttribute('data-open');
      if (trig) trig.setAttribute('aria-expanded', 'false');
      if (!panel) return;
      if (reduceMotion) { panel.hidden = true; return; }
      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { panel.style.height = '0px'; });
      });
      panel.addEventListener('transitionend', function h(ev) {
        if (ev.propertyName !== 'height') return;
        panel.hidden = true;
        panel.removeEventListener('transitionend', h);
      });
    }
  }

  /* ---------- 5. Count-up numbers --------------------------------------- */
  function initCounters() {
    var els = document.querySelectorAll('[data-vv-count]');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.textContent = el.getAttribute('data-vv-count'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        io.unobserve(el);
        var target = parseFloat(el.getAttribute('data-vv-count')) || 0;
        var dec = (el.getAttribute('data-vv-count').split('.')[1] || '').length;
        var start = performance.now(), dur = 1600;
        (function tick(now) {
          var p = Math.min((now - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = (target * eased).toFixed(dec);
          if (p < 1) requestAnimationFrame(tick);
        })(start);
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 6. Sticky header state ------------------------------------ */
  function initSticky() {
    var header = document.querySelector('[data-vv-sticky-header]');
    if (!header) return;
    var last = 0, ticking = false;
    function update() {
      var y = window.scrollY || window.pageYOffset;
      header.classList.toggle('is-stuck', y > 12);
      if (header.getAttribute('data-vv-hide-on-scroll') === 'true') {
        header.classList.toggle('is-hidden', y > last && y > 240 && !header.contains(document.activeElement));
      }
      last = y; ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- 7. Focus trap helper (drawers / modals) -------------------- */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
  window.vvTrapFocus = function (container, onEscape) {
    var items = container.querySelectorAll(FOCUSABLE);
    if (!items.length) return function () {};
    var first = items[0], last = items[items.length - 1];
    function handler(e) {
      if (e.key === 'Escape' && onEscape) { onEscape(); return; }
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handler);
    first.focus();
    return function () { container.removeEventListener('keydown', handler); };
  };

  /* ---------- 7b. FAQ category tabs ------------------------------------- */
  function initFaqTabs() {
    document.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-vv-faqtab]');
      if (!tab) return;
      var root = tab.closest('[data-vv-faqtabs]');
      if (!root) return;
      var index = tab.getAttribute('data-vv-faqtab');

      root.querySelectorAll('[data-vv-faqtab]').forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-vv-faqpanel]').forEach(function (p) {
        var on = p.getAttribute('data-vv-faqpanel') === index;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });
    });

    // Left/right arrows move between tabs, per the ARIA tablist pattern.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      var tab = e.target.closest('[data-vv-faqtab]');
      if (!tab) return;
      var root = tab.closest('[data-vv-faqtabs]');
      if (!root) return;
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-vv-faqtab]'));
      var i = tabs.indexOf(tab);
      var next = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      if (!next) return;
      e.preventDefault();
      next.click();
      next.focus();
    });
  }

  /* ---------- 8. Tiny global event bus ---------------------------------- */
  window.vvBus = {
    on: function (n, f) { document.addEventListener('vv:' + n, f); },
    emit: function (n, d) { document.dispatchEvent(new CustomEvent('vv:' + n, { detail: d })); }
  };

  /* ---------- Boot ------------------------------------------------------- */
  function boot() {
    initReveal(); initThreads(); initImages();
    initAccordions(); initCounters(); initSticky(); initFaqTabs();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }

  // Re-boot inside the theme editor when a section is re-rendered
  document.addEventListener('shopify:section:load', function () {
    initReveal(); initThreads(); initImages(); initCounters(); initSticky();
  });
})();
